import { Button, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { CheckboxProps } from "antd/lib/checkbox";
import React, {
  FunctionComponent,
  ReactText,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getCognitoUserData,
  resendConfirmationEmail,
} from "../../features/auth/authSlice";
import {
  fetchCompanies,
  selectCompanies,
  selectCompaniesAreFetching,
} from "../../features/company/companySlice";
import { fetchGroups, selectGroupsDict } from "../../features/groups/groupSlice";
import {
  disableUsersStatus,
  enableUsersStatus,
  IUser,
  RolesDict,
  selectAreUsersFetching,
  selectUsers,
  selectUsersIds,
  UserRole,
  fetchUsers,
  UsersGroups,
  selectFilterTextForUsers,
  selectFilterCompanyIdForUsers,
  setFilterTextForUsers,
  setFilterCompanyIdForUsers,
  deleteUserById,
  selectIsDeletingUser,
} from "../../features/users/usersSlice";
import { PAGE_SIZE } from "../../utils/constants";
import { Status, StatusEnum } from "../common/Status";
import tableStyles from "../common/table.module.scss";
import { TableTools } from "../common/TableTools";
import { CompanyFilter } from "../Filters/Company";
import { useDebounceSelector } from "../utils/useDebounce";
import { NamePlusThumbnail } from "./common/NamePlusThumbnail";
import { ActionSuffixes, Prefixes } from "../prefixes";
import { getTranslations } from "../../features/translations/translationsUtils";
import { DialogPopUpConfirmation } from "../common/DialogPopUpConfirmation";


export const UsersList: FunctionComponent = () => {
  const cognitoUserData = useSelector(getCognitoUserData);
  const companiesDict = useSelector(selectCompanies);
  const usersIdsList = useSelector(selectUsersIds);
  const usersDict = useSelector(selectUsers);
  const groupsDict = useSelector(selectGroupsDict);
  const usersList = usersIdsList.map((userId) => ({
    ...usersDict[userId],
    companyName: companiesDict[usersDict[userId].company ?? ""]?.name,
    studentGroupName:
      groupsDict[usersDict[userId].studentGroupId ?? ""]?.name,
  }));
  const areCompaniesFetching = useSelector(selectCompaniesAreFetching);
  const areUsersFetching = useSelector(selectAreUsersFetching);
  const filterValue = useSelector(selectFilterTextForUsers);
  const filteredInfo = useSelector(selectFilterCompanyIdForUsers);

  const debounceFilterValue = useDebounceSelector(selectFilterTextForUsers, 500);
  const history = useHistory();
  const [selectedRowKeys, selectRowKeys] = useState<ReactText[]>([]);
  const [showActivationBtns, setShowActivationBtns] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState('');
  const isDeletingUser = useSelector(selectIsDeletingUser);
  const dispatch = useDispatch();
  const role = cognitoUserData ? cognitoUserData["custom:role"] : undefined;
  const isSuperAdmin = role === UserRole.SuperAdmin;

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      setShowActivationBtns(true);
    } else {
      setShowActivationBtns(false);
    }
  }, [selectedRowKeys]);


  const onEdit = (userId: string) => {
    history.push(`${Prefixes.user}/${userId}${ActionSuffixes.edit}`);
  };


  const onDelete = (userId: string) => {
    dispatch(deleteUserById(userId));
  };

  const columns: ColumnsType<IUser> = [
    {
      title: getTranslations('LIST_FULL_NAME'),
      dataIndex: "name",
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "ascend",
      filteredValue: debounceFilterValue,
      ellipsis: true,
      sorter: (a: IUser, b: IUser) => a.name.localeCompare(b.name),
      onFilter: (value, record) => {
        return (
          record.name.toLowerCase().includes(value.toString().toLowerCase()) ||
          record.email.toLowerCase().includes(value.toString().toLowerCase())
        );
      },
      render: (value, user) => {
        return <NamePlusThumbnail value={value} user={user} />;
      },
    },
    {
      title: getTranslations('LIST_FULL_ROLE'),
      dataIndex: "role",
      render: (value, values) => {
        if (values.groups?.includes(UsersGroups.Administrators)) {
          return RolesDict[value as UserRole];
        }
        return 'Estudiante';
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: getTranslations('LIST_STATUS'),
      dataIndex: "enabled",
      render: (status) => {
        return (
          <Status status={status ? StatusEnum.Enable : StatusEnum.Disable} />
        );
      },
      sortDirections: ["descend", "ascend"],
      sorter: (a: IUser, b: IUser) =>
        a.enabled === b.enabled ? 0 : a.enabled > b.enabled ? 1 : -1,
    },
    {
      title: getTranslations('LIST_GROUP'),
      dataIndex: "studentGroupName",
      ellipsis: true,
      sortDirections: ["descend", "ascend"],
      // filteredValue: filteredInfo.length > 1 ? filteredInfo : [],
      sorter: (a: any, b: any) =>
        (a.studentGroupName || "").localeCompare(b.studentGroupName || ""),
      onFilter: (value, record: IUser) =>
        record.studentGroupId === (value as string),
    },
    {
      title: getTranslations('LIST_COMPANY'),
      dataIndex: "companyName",
      sortDirections: ["descend", "ascend"],
      filteredValue: filteredInfo ? [filteredInfo] : undefined,
      onFilter: (value, record: IUser) => record.company === (value as string),
      sorter: (a: IUser, b: IUser) =>
        (a.companyName || "").localeCompare(b.companyName || ""),
      render: (value, user) => {
        return (
          <React.Fragment>
            {value}
            <div className={tableStyles.actions}>
              {user.status === "FORCE_CHANGE_PASSWORD" ? (
                <Button
                  type="default"
                  onClick={() => {
                    dispatch(resendConfirmationEmail(user.username));
                  }}
                >
                  {getTranslations('USER_RESEND_INVITATION')}
                </Button>
              ) : null}
              <Button
                type="default"
                onClick={() => {
                  onEdit(user.username);
                }}
              >
                {getTranslations('LIST_EDIT')}
              </Button>
              {!isSuperAdmin || user.enabled ? null : <Button
                type="default"
                onClick={() => {
                  setUserToDelete(user.username);
                  setShowConfirmationModal(true);
                }}
              >
                {getTranslations('USER_DELETE')}
              </Button>}
            </div>
          </React.Fragment>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchCompanies());
    dispatch(fetchGroups());
  }, [dispatch]);
  const cognitoUsername = cognitoUserData
    ? cognitoUserData["cognito:username"]
    : null;
  const rowSelection = {
    getCheckboxProps: (record: IUser): CheckboxProps => {
      if (record.username === cognitoUsername) {
        return {
          disabled: true,
        };
      }
      return {};
    },
    selectedRowKeys,
    onChange: (selectedRowKeys: ReactText[]) => {
      selectRowKeys(selectedRowKeys);
    },
  };

  const enableStatus = () => {
    const data = {
      usernames: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(enableUsersStatus(true, data));
  };

  const disableStatus = () => {
    const data = {
      usernames: selectedRowKeys.map((key) => key.toString()),
    };
    dispatch(disableUsersStatus(true, data));
  };
  const setFilterValue = (value: string[]) => {
    dispatch(setFilterTextForUsers(value[0] || ""));
  }

  const setFilteredInfo = (value: string) => {
    dispatch(setFilterCompanyIdForUsers(value || ""));
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    setUserToDelete('');
  };
  const handleOnDelete = () => {
    setShowConfirmationModal(false);
    onDelete(userToDelete);
    setUserToDelete('');
  };

  return (
    <React.Fragment>

      <DialogPopUpConfirmation
        name={getTranslations('USER_DELETE_CONFIRMATION')}
        visible={showConfirmationModal || isDeletingUser }
        onCancel={handleCloseConfirmationModal}
        cancelText={getTranslations('USER_DELETE_CANCEL')}
        okText={getTranslations('USER_DELETE_OK')}
        onOk={handleOnDelete}
        loading={isDeletingUser}
      />
      <TableTools
        placeholder={getTranslations('LIST_SEARCH_IN_TEURONA')}
        onSearch={(value) => setFilterValue([value])}
        onChange={(event) => setFilterValue([event.target.value])}
        onEnable={() => enableStatus()}
        onDisable={() => disableStatus()}
        filter={<CompanyFilter onApply={setFilteredInfo} value={filteredInfo} />}
        activationBtnsVisible={showActivationBtns}
        value={filterValue}
      />
      <Table<IUser>
        loading={areCompaniesFetching || areUsersFetching}
        className={tableStyles.table}
        rowClassName={() => tableStyles.row}
        rowKey="username"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={usersList}
        pagination={{ position: ["bottomRight"], pageSize: PAGE_SIZE, showSizeChanger: false }}
      />
    </React.Fragment>
  );
};
